import { DaySequence, DaySequenceStatus } from '../generated/gql-types';

/** Worker checked in but no final or proposed check-out yet. → {@link clockAction} (checkout). */
export const isMissingCheckOut = (
  daySequence: Pick<
    DaySequence,
    'clockInTime' | 'clockOutTime' | 'proposedClockOutTime'
  >,
): boolean =>
  !!daySequence.clockInTime &&
  !daySequence.clockOutTime &&
  !daySequence.proposedClockOutTime;

/** Both clock times recorded on the day sequence row from worker. */
export const hasLoggedTimesheet = (
  daySequence: Pick<DaySequence, 'clockInTime' | 'clockOutTime'>,
): boolean => !!daySequence.clockInTime && !!daySequence.clockOutTime;

/** Complete proposed clock-in and clock-out pair. */
export const hasProposedTimesheet = (
  daySequence: Pick<
    DaySequence,
    'proposedClockInTime' | 'proposedClockOutTime'
  >,
): boolean =>
  !!daySequence.proposedClockInTime && !!daySequence.proposedClockOutTime;

export const hasTimesheetTimesToActOn = (
  daySequence: Pick<
    DaySequence,
    | 'clockInTime'
    | 'clockOutTime'
    | 'proposedClockInTime'
    | 'proposedClockOutTime'
  >,
): boolean =>
  hasLoggedTimesheet(daySequence) || hasProposedTimesheet(daySequence);

export const getTimesheetTimesForApprove = (
  daySequence: Pick<
    DaySequence,
    | 'status'
    | 'clockInTime'
    | 'clockOutTime'
    | 'breaks'
    | 'proposedClockInTime'
    | 'proposedClockOutTime'
    | 'proposedBreaks'
  >,
): {
  clockInTime: string;
  clockOutTime: string;
  breaks: DaySequence['breaks'] | DaySequence['proposedBreaks'] | null;
} => {
  const proposedBreaks = daySequence.proposedBreaks ?? [];
  const copiesProposedOntoActuals =
    daySequence.status === DaySequenceStatus.ProposedChange &&
    (hasProposedTimesheet(daySequence) || proposedBreaks.length > 0);

  if (copiesProposedOntoActuals) {
    return {
      clockInTime: daySequence.proposedClockInTime || '',
      clockOutTime: daySequence.proposedClockOutTime || '',
      breaks: proposedBreaks.length > 0 ? proposedBreaks : null,
    };
  }

  return {
    clockInTime:
      daySequence.clockInTime || daySequence.proposedClockInTime || '',
    clockOutTime:
      daySequence.clockOutTime || daySequence.proposedClockOutTime || '',
    breaks: daySequence.breaks?.length
      ? daySequence.breaks
      : daySequence.proposedBreaks || null,
  };
};

const isApprovedStatus = (status: DaySequenceStatus): boolean =>
  status === DaySequenceStatus.ReadyForPay ||
  status === DaySequenceStatus.PayProcessed ||
  status === DaySequenceStatus.Completed;

const isClientDisputedStatus = (status: DaySequenceStatus): boolean =>
  status === DaySequenceStatus.ProposedChange ||
  status === DaySequenceStatus.EscalatedToTenant;

export type TimesheetEditAction = 'propose' | 'resolve';

export type TimesheetEditConfirmationVariant = 'propose' | 'resolve' | 'edit';

const TENANT_DISPUTED_TIMESHEET_STATUSES = new Set<DaySequenceStatus>([
  DaySequenceStatus.EscalatedToTenant,
  DaySequenceStatus.ProposedChange,
]);

/** Tenant edit flows that adjust client/worker disputed times (vs routine correction). */
export const isTenantDisputedTimesheetStatus = (
  status: DaySequenceStatus | null | undefined,
): boolean => status != null && TENANT_DISPUTED_TIMESHEET_STATUSES.has(status);

export const getTimesheetEditModalTitle = (
  editAction: TimesheetEditAction,
  status?: DaySequenceStatus | null,
): string => {
  if (editAction === 'propose') {
    return 'Edit Timesheet';
  }
  return isTenantDisputedTimesheetStatus(status)
    ? 'Resolve Timesheet'
    : 'Edit Timesheet';
};

export const getTimesheetEditConfirmationVariant = (
  editAction: TimesheetEditAction,
  status?: DaySequenceStatus | null,
): TimesheetEditConfirmationVariant => {
  if (editAction === 'propose') {
    return 'propose';
  }
  return isTenantDisputedTimesheetStatus(status) ? 'resolve' : 'edit';
};

export type TimesheetCardActions = {
  showCheckout: boolean;
  showEdit: boolean;
  showApprove: boolean;
  showDelete: boolean;
  /** Which mutation the edit modal should submit. */
  editAction: TimesheetEditAction | null;
};

/**
 * Which timesheet buttons to show and which backend mutation each maps to.
 *
 * | Scenario | Endpoint |
 * |----------|----------|
 * | Missing check-out | `clockAction` (existing clock-in + new clock-out) |
 * | Client backfill / create (no existing day sequences) | `clockAction` |
 * | Tenant backfill / create (no existing day sequences) | `createDaySequences` |
 * | Client edit logged times | `proposeDaySequenceChanges` |
 * | Tenant edit logged / disputed times | `resolveDaySequenceDisputes` |
 * | Approve logged / proposed times | `approveDaySequences` |
 */
export const getTimesheetCardActions = (
  daySequence: DaySequence,
  isClientPortal: boolean,
): TimesheetCardActions => {
  if (isMissingCheckOut(daySequence)) {
    return {
      showCheckout: true,
      showEdit: false,
      showApprove: false,
      showDelete: !isClientPortal,
      editAction: null,
    };
  }

  const hasTimesToActOn = hasTimesheetTimesToActOn(daySequence);

  if (isClientPortal) {
    if (
      isApprovedStatus(daySequence.status) ||
      isClientDisputedStatus(daySequence.status) ||
      !hasTimesToActOn
    ) {
      return {
        showCheckout: false,
        showEdit: false,
        showApprove: false,
        showDelete: false,
        editAction: null,
      };
    }

    return {
      showCheckout: false,
      showEdit: true,
      showApprove: daySequence.status === DaySequenceStatus.RequiresApproval,
      showDelete: false,
      editAction: 'propose',
    };
  }

  const canApprove =
    daySequence.status === DaySequenceStatus.RequiresApproval ||
    daySequence.status === DaySequenceStatus.ProposedChange ||
    daySequence.status === DaySequenceStatus.EscalatedToTenant;

  const canEdit =
    daySequence.status === DaySequenceStatus.EscalatedToTenant ||
    daySequence.status === DaySequenceStatus.ProposedChange ||
    daySequence.status === DaySequenceStatus.RequiresApproval ||
    daySequence.status === DaySequenceStatus.ReadyForPay ||
    daySequence.status === DaySequenceStatus.InProgress ||
    daySequence.status === DaySequenceStatus.ErrorEncountered ||
    daySequence.status === DaySequenceStatus.ManualInterventionRequired ||
    daySequence.status === DaySequenceStatus.Pending;

  const showEdit = canEdit && hasTimesToActOn;

  return {
    showCheckout: false,
    showEdit,
    showApprove: canApprove && hasTimesToActOn,
    showDelete: true,
    editAction: showEdit ? 'resolve' : null,
  };
};
